import React from 'react';
import { dynastyTheme } from '../../../../../services/colorService';
import PropTypes from 'prop-types';
import { mobileTableStyles, mobileColors, mobileTextStyles } from '../../../../../services/mobileStyleService';
import { formatBattingAvg, formatERA } from '../../../../../services/statFormatUtils';
import { MobileDynastyTable } from '../../MobileDynastyTable';
import { computeTotals } from '../../../../../services/tables/statTotals';

/**
 * StatsTabContent (modal) — Season + Last-14 StatBlocks and a Career table,
 * rebuilt to the package Stats mockup (Unit 2a.1 addendum).
 *
 * House treatment, single-sourced — ZERO hardcoded shading in this tab:
 *  - Season / Last-14 are flat StatBlocks: ONE surface (mobileTableStyles.rows.even)
 *    for the whole strip, so the frozen first column reads as the SAME solid as its
 *    row (Unit 0 sticky-equals-row rule) — no stray gray column-one cell. Header
 *    labels = mobileColors.textMuted. Right-edge inline scroll fade.
 *  - Career renders through the MobileDynastyTable primitive in its token-driven
 *    BORDERLESS mode (no zebra: flat row + faint inter-row hairline + slight header
 *    shade), ordered OLDEST→NEWEST with the season TOTAL row at the bottom.
 *  - 4px modal boundary is provided structurally by the modal body wrapper.
 */

const C = dynastyTheme.tokens.colors;
const GOLD = dynastyTheme.tokens.colors.primary;
// StatBlock surface. The mockup's #15151b read as a discolored column-1 block
// because the rest of the row was transparent — this OVERRIDES it: one opaque
// dark (the house row solid) for the WHOLE strip, applied identically to the row
// AND its sticky first cell, so column 1 == row with zero seam (Unit 0 rule).
// Match the flat modal background (#0d0d14) so the strip is NOT a lighter band
// sitting on the darker modal — it blends in and only the section hairline shows.
const STRIP_BG = mobileTableStyles.rows.odd; // #0d0d14 — identical to the modal surface
const STRIP_HEADER = C.muted;  // header label color
const STRIP_VALUE = C.coolGray[150];   // value cell color
const STRIP_FADE = C.ground.screen;    // right-edge fade target (matches the modal)
const SECTION_HAIRLINE = `1px solid ${C.hairline}`; // thin inter-section line

const HITTER_COLS = ['G', 'AB', 'R', 'H', 'HR', 'RBI', 'SB', 'BB', 'SO', 'AVG', 'OPS'];
const HITTER_KEYS = ['games_played', 'at_bats', 'runs', 'hits', 'home_runs', 'rbi', 'stolen_bases', 'walks', 'strikeouts', 'batting_avg', 'ops'];
const PITCHER_COLS = ['G', 'GS', 'W', 'L', 'SV', 'IP', 'H', 'ER', 'BB', 'SO', 'QS', 'ERA', 'WHIP'];
const PITCHER_KEYS = ['games_played', 'games_started', 'wins', 'losses', 'saves', 'innings_pitched', 'hits_allowed', 'earned_runs', 'walks_allowed', 'strikeouts_pitched', 'quality_starts', 'era', 'whip'];

const fmtVal = (key, val) => {
  if (val === null || val === undefined) return '-';
  if (['batting_avg', 'obp', 'slg', 'ops', 'whip'].includes(key)) return formatBattingAvg(val);
  if (key === 'era') return formatERA(val);
  if (key === 'innings_pitched') return typeof val === 'number' ? val.toFixed(1) : val;
  return typeof val === 'number' ? Math.round(val) : val;
};

const sectionHdr = { color: GOLD, ...mobileTextStyles.sectionHeader, margin: 0 };
// EVERY cell carries the opaque STRIP_BG (sticky AND non-sticky, header AND
// body) so the whole row is one continuous solid — no transparent cells, no
// reliance on a table-level background bleeding through (which, under collapsed
// borders, composited into a visible column-1 seam). Pair with borderCollapse
// 'separate' + borderSpacing 0 on the table below.
const thStyle = { color: STRIP_HEADER, padding: '3px 6px', textAlign: 'center', whiteSpace: 'nowrap', background: STRIP_BG, ...mobileTableStyles.headerText };
const tdStyle = { color: STRIP_VALUE, padding: '4px 6px', textAlign: 'center', fontSize: '11px', whiteSpace: 'nowrap', background: STRIP_BG };
const stickyThStyle = { ...thStyle, textAlign: 'left', padding: '3px 5px', position: 'sticky', left: 0, background: STRIP_BG, zIndex: 1 };
const stickyTdStyle = { ...tdStyle, textAlign: 'left', padding: '4px 5px', position: 'sticky', left: 0, background: STRIP_BG, zIndex: 1, fontWeight: 700 };
const scrollWrap = { overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' };
// Hairline-separated section: a thin top line + 8px breathing room (NOT a box).
const sectionWrap = { borderTop: SECTION_HAIRLINE, paddingTop: '8px' };

// One flat StatBlock strip (Season / Last-14). label = the row label; getColor
// maps a stat key+value → cell color (L14 standout). Right-edge fade signals more.
// No box, no per-cell zebra (single-row strip); sticky first column = #15151b.
const StatBlock = ({ label, labelColor, cols, keys, stats, getColor }) => (
  <div style={{ position: 'relative' }}>
    <div className="stats-scroll" style={scrollWrap}>
      {/* borderCollapse 'separate' + every cell opaque (above) → the sticky first
          cell is byte-identical to its row, no collapsed-border compositing seam.
          No table-level background: each cell paints its own STRIP_BG. */}
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: '11px', minWidth: '100%', width: 'max-content' }}>
        <thead><tr>
          <th style={stickyThStyle} />
          {cols.map((c) => <th key={c} style={thStyle}>{c}</th>)}
        </tr></thead>
        <tbody>
          <tr>
            <td style={stickyTdStyle}><span style={{ color: labelColor, fontWeight: 700 }}>{label}</span></td>
            {keys.map((k) => (
              <td key={k} style={{ ...tdStyle, color: getColor ? getColor(k, stats?.[k]) : tdStyle.color }}>{fmtVal(k, stats?.[k])}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
    {/* inline scroll-fade affordance (right edge) → #13131a, with a gold › */}
    <span aria-hidden="true" style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: 20, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 2, color: GOLD, fontSize: 12, fontWeight: 700, background: `linear-gradient(90deg, transparent, ${STRIP_FADE})` }}>›</span>
  </div>
);

const StatsTabContent = ({ player, statsSeasonYear }) => {
  const isPitcher = ['P', 'SP', 'RP'].includes(player?.info?.position);
  const cols = isPitcher ? PITCHER_COLS : HITTER_COLS;
  const keys = isPitcher ? PITCHER_KEYS : HITTER_KEYS;

  const seasonStats = isPitcher ? player?.stats?.season?.pitching : player?.stats?.season?.hitting;
  const rolling14 = isPitcher ? player?.stats?.rolling_14_day?.pitching : player?.stats?.rolling_14_day?.hitting;

  const careerStats = player?.career_stats;
  // OLDEST → NEWEST (ascending), Total row appended last.
  const sortedCareer = [...(careerStats ?? [])].sort((a, b) => (a.season || 0) - (b.season || 0));
  const seasonLabel = statsSeasonYear || player?.stats_season_year || sortedCareer[sortedCareer.length - 1]?.season || new Date().getFullYear();

  // DD-STAT-CONSOLIDATION C3: career TOTAL via the SSoT engine — sums components and
  // recomputes batting_avg/OBP/SLG/OPS (or ERA/WHIP). Fixes the missing OPS (was never
  // computed → '-') and adds correct OBP/SLG.
  const careerTotals = computeTotals(
    sortedCareer.map((yr) => (isPitcher ? yr.pitching : yr.hitting) || {}),
    { statType: isPitcher ? 'pitching' : 'hitting' },
  );

  // All years oldest→newest, then a synthetic TOTAL row (kept — the live version
  // has it). The single column set drives both data rows and the totals line.
  const careerData = sortedCareer.length > 0 ? [...sortedCareer, { __isTotal: true }] : [];
  const careerColumns = [
    {
      key: 'season', header: 'Year', width: '44px', textAlign: 'left',
      render: (v, row) => row.__isTotal
        ? <span style={{ color: GOLD, fontWeight: 700 }}>Total</span>
        : <span style={{ color: row.season === seasonLabel ? GOLD : mobileColors.textSecondary }}>{row.season || '-'}</span>,
    },
    {
      key: 'team', header: 'Tm', width: '34px',
      render: (v, row) => row.__isTotal ? `${sortedCareer.length}yr` : (row.team || row.mlb_team || '-'),
    },
    ...cols.map((c, i) => {
      const k = keys[i];
      return {
        key: k, header: c, width: '34px',
        render: (v, row) => {
          const s = row.__isTotal ? careerTotals : (isPitcher ? (row.pitching ?? {}) : (row.hitting ?? {}));
          const val = fmtVal(k, s[k]);
          return <span style={row.__isTotal ? { color: GOLD, fontWeight: 600 } : undefined}>{val}</span>;
        },
      };
    }),
  ];

  // Last-14 standout coloring (a 14-day value above the season value on a
  // "more is better" stat reads green).
  const l14Color = (k, val) => {
    const seasonVal = seasonStats?.[k];
    const standout = typeof val === 'number' && typeof seasonVal === 'number' && val > seasonVal
      && !['games_played', 'games_started', 'at_bats', 'strikeouts', 'losses', 'earned_runs', 'hits_allowed', 'walks_allowed'].includes(k);
    return standout ? mobileColors.success : mobileColors.textTertiary;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <style>{`.stats-scroll::-webkit-scrollbar { display: none; }`}</style>

      {/* Season — hairline-separated StatBlock section (no box) */}
      <div style={{ ...sectionWrap, paddingBottom: 8 }}>
        <div style={{ marginBottom: 4 }}><span style={sectionHdr}>{seasonLabel} Season</span></div>
        <StatBlock label="Season" labelColor={GOLD} cols={cols} keys={keys} stats={seasonStats} />
      </div>

      {/* Last 14 days */}
      <div style={{ ...sectionWrap, paddingBottom: 8 }}>
        <div style={{ marginBottom: 4 }}><span style={sectionHdr}>Last 14 days</span></div>
        {rolling14 ? (
          <StatBlock label="L14" labelColor={mobileColors.success} cols={cols} keys={keys} stats={rolling14} getColor={l14Color} />
        ) : (
          <div style={{ padding: '8px 0', textAlign: 'center', color: mobileColors.textMuted, fontSize: 10 }}>No L14 data yet</div>
        )}
      </div>

      {/* Career — primitive, BORDERLESS mode, oldest→newest, Total last */}
      {sortedCareer.length > 0 && (
        <div style={{ ...sectionWrap, paddingBottom: 8 }}>
          <div style={{ marginBottom: 4 }}><span style={sectionHdr}>Career</span></div>
          <MobileDynastyTable
            data={careerData}
            columns={careerColumns}
            keyField="season"
            tableType="flat-data"
            rowVariant="borderless"
            maxHeight="240px"
            stickyHeader
          />
        </div>
      )}

      {(!seasonStats || Object.keys(seasonStats).length === 0) && sortedCareer.length === 0 && (
        <div style={{ padding: '30px 0', textAlign: 'center', color: mobileColors.textMuted, fontSize: 12 }}>No stats available</div>
      )}
    </div>
  );
};

StatsTabContent.propTypes = {
  player: PropTypes.object.isRequired,
  playerId: PropTypes.string,
  leagueId: PropTypes.string,
  statsSeasonYear: PropTypes.number,
};

export default StatsTabContent;
